// BRK-S132 — the bond wizard's SPINE, as data.
//
// The spine was a one-liner in `NewBondSubmissionPage`:
//
//     ["Eligibility", ...config.steps.map((s) => s.title), "Project & bond"]
//
// which is not just a list of labels. It is the wizard's whole addressing
// scheme: the page's `step` state is an index into it, and `steps[step - 1]` is
// how every surface finds the configuration step it is rendering. Both of those
// encode a rule — ONE configuration step is ONE chip — and the `preapproval`
// step breaks it: Greg's wireframe has the broker walk
//
//     Eligibility ✓ Business ✓ Indemnitor ✓ Checks ✓ ⑤ Indication
//
// where Checks and Indication are two chips over one configured step.
//
// So the spine becomes a list of CHIPS, each carrying the step it renders
// (rather than an offset the reader has to un-do), and `step` indexes chips.
// The `- 1` arithmetic is gone, which is the point: a second place that
// converted between the two numbering schemes is how a step gets skipped.
//
// Pure, and in `lib/` like `submissionFlow.ts`, so the mapping is unit-tested
// without mounting a wizard (NN #1).
import type { BondFlow, BondStep } from "@/lib/brokerBonds";
import { stepKind, type BondStepRun } from "@/lib/brokerBonds";
import {
  stepOpensForEdit,
  type ChangeDeclaration,
} from "@/lib/bondRepeatRequest";

/** The first chip: code-owned eligibility + bond-type + insured. */
export const ELIGIBILITY_TITLE = "Eligibility";
/** The last chip: the code-owned project-&-bond terms. */
export const FINAL_TITLE = "Project & bond";
/** The two chips a `preapproval` step becomes. Code-owned words: one step
 *  cannot supply two titles, and these are the wireframe's own. */
export const PREAPPROVAL_CHECKS_TITLE = "Checks";
export const PREAPPROVAL_INDICATION_TITLE = "Indication";
/** The repeat request's own chip (BRK-S162). Code-owned words: it renders no
 *  configuration step, so no step can supply its title. */
export const SUMMARY_TITLE = "Summary";

/**
 * One chip on the stepper, and the surface behind it.
 *
 * A discriminated union rather than `{title, stepIndex}`: the page renders a
 * different component per kind, and a kind it does not handle should not
 * compile. `step` is the CONFIGURATION step this chip renders — carried, not
 * looked up, so nothing has to reconstruct the offset.
 */
export type BondChip =
  | { kind: "eligibility"; id: string; title: string }
  /** The repeat request's read-only summary and its one question (BRK-S162).
   *  It renders no configuration step — it is ABOUT the fields steps behind
   *  it, which is why it carries none. */
  | { kind: "summary"; id: string; title: string }
  /** A `fields`, `data_pull`, or unsupported step — one chip, its own title. */
  | { kind: "step"; id: string; title: string; step: BondStep }
  | {
      kind: "preapproval_checks";
      id: string;
      title: string;
      step: BondStep;
    }
  | {
      kind: "preapproval_indication";
      id: string;
      title: string;
      step: BondStep;
    }
  | { kind: "final"; id: string; title: string };

export interface BondChipOptions {
  /**
   * Omit the preapproval step's two chips entirely (BRK-S134).
   *
   * True when the INSURED already holds a granted preapproval, so this order
   * ran nothing. Omitted rather than marked done: a ✓ on a chip is a claim that
   * this order did the work, and it did not.
   */
  skipPreapproval?: boolean;
  /**
   * Which flow the resolved configuration declares (BRK-S160).
   *
   * Absent (or `bond`) is the spine this function has always produced, to the
   * chip. `preapproval` is the STANDALONE flow: no terms chip, and the
   * Indication chip is the end of the walk.
   */
  flow?: BondFlow;
  /**
   * The REPEAT request's spine (BRK-S162).
   *
   * Present only when the insured already holds a standing program, which is
   * the same condition `skipPreapproval` reads — the two travel together, and
   * they are separate options because they do separate things: that one drops
   * the two preapproval chips, this one inserts the Summary and closes the
   * fields steps the broker has not said changed.
   *
   * `declaration: null` is "they have not answered yet", which closes every
   * summarised step. The spine therefore GROWS as they answer, which is the
   * behaviour the screen describes ("you'll review and update the business
   * information") rather than a list that was always there and was hidden.
   *
   * Absent is byte-identical to every spine this function produced before the
   * story — asserted, not intended.
   */
  repeat?: {
    declaration: ChangeDeclaration | null;
    /** Summarised steps a refused save OPENED whatever the answer said
     *  (BRK-S268) — the step a refusal is about has to be somewhere the broker
     *  can fix it. Absent is none. */
    reopened?: ReadonlySet<string>;
  };
}

/**
 * The chips for a resolved configuration.
 *
 * ── THE THIRD SPINE (BRK-S162) ───────────────────────────────────────────────
 *
 *   repeat  Eligibility  Summary  [only what changed]  … data pulls …  Project & bond
 *
 * An insured who already holds a standing program has answered all of this
 * before. `repeat` inserts the Summary chip and closes every fields step the
 * broker has not declared changed; a data pull is untouched, because it is not
 * information anybody restates and dropping one would skip work the
 * configuration declared.
 *
 * An unsupported step still gets a chip. Dropping it would renumber every chip
 * behind it and silently skip whatever it was asking for — the wizard would
 * look complete while a step the configuration declared never happened.
 *
 * ── THE TWO SPINES (BRK-S160) ────────────────────────────────────────────────
 *
 *   bond         Eligibility … configured steps … Project & bond
 *   preapproval  Eligibility … configured steps … Indication
 *
 * The standalone flow ENDS on Indication, so there is no `final` chip to append
 * and the Indication chip is never omitted — not even for an insured who
 * already holds a program, where the bond flow drops both preapproval chips
 * (`skipPreapproval`). Dropping it there would leave a wizard whose last chip
 * is a fields step and whose footer offers nothing: a request the broker could
 * not finish. Checks IS still omitted, for the reason it always was — nothing
 * ran, and a ✓ would claim it did — so the standalone flow simply shows the
 * standing decision as its terminal screen.
 *
 * ── AND THE SHAPE DISCOVERY FORBIDS ──────────────────────────────────────────
 *
 * A `preapproval` flow whose configuration declares NO preapproval step has no
 * terminal chip to end on. Discovery does not produce it, which is exactly why
 * it is handled: the wizard is what a broker is standing in, and a spine with
 * no ending is a dead end rather than an error message. So the bond spine is
 * used — terms and all — and the broker finishes an ordinary order.
 */
export function bondChips(
  steps: readonly BondStep[],
  options: BondChipOptions = {},
): BondChip[] {
  // The flow only takes effect when the configuration can actually serve it.
  const standalone =
    options.flow === "preapproval" &&
    steps.some((step) => stepKind(step) === "preapproval");
  const chips: BondChip[] = [
    { kind: "eligibility", id: "eligibility", title: ELIGIBILITY_TITLE },
  ];
  // SECOND, and before every configured step: the summary is what the broker
  // reads in order to answer, and the steps it closes or opens all sit behind
  // it. Putting it later would ask the question after the work it is about.
  if (options.repeat) {
    chips.push({ kind: "summary", id: "summary", title: SUMMARY_TITLE });
  }
  for (const step of steps) {
    if (
      options.repeat &&
      !stepOpensForEdit(
        step,
        options.repeat.declaration,
        options.repeat.reopened,
      )
    ) {
      // OMITTED, not disabled. A greyed chip for a step the broker said did
      // not change is a claim that something is owed on it; there is nothing
      // owed, and the values PMS prefilled are the values that file.
      continue;
    }
    if (stepKind(step) === "preapproval") {
      if (!options.skipPreapproval) {
        chips.push({
          kind: "preapproval_checks",
          id: `${step.key}:checks`,
          title: PREAPPROVAL_CHECKS_TITLE,
          step,
        });
      }
      if (!options.skipPreapproval || standalone) {
        chips.push({
          kind: "preapproval_indication",
          id: `${step.key}:indication`,
          title: PREAPPROVAL_INDICATION_TITLE,
          step,
        });
      }
      continue;
    }
    chips.push({ kind: "step", id: step.key, title: step.title, step });
  }
  if (!standalone)
    chips.push({ kind: "final", id: "final", title: FINAL_TITLE });
  return chips;
}

/** Just the labels, for the stepper. */
export function chipTitles(chips: readonly BondChip[]): string[] {
  return chips.map((c) => c.title);
}

/**
 * Where **Back** goes from a chip — one step back, except that it never lands
 * on the preapproval CHECKS chip.
 *
 * The Checks chip AUTO-ADVANCES the moment its run has settled, which is the
 * right behaviour going forward (there is no verdict there for the broker to
 * acknowledge) and a bounce going backwards: Back from Indication would land on
 * Checks, replay the settled run, and immediately advance forward again. The
 * broker would be unable to get behind their indication at all — a Back button
 * that visibly does nothing.
 *
 * So it is skipped, which also reads correctly: a review that has already run
 * is not a screen anybody goes back to change.
 *
 * Never below 1. Chip 0 is eligibility, and once an order exists that chip is
 * behind the broker for good — stepping onto it would offer to start a SECOND
 * order for the same insured (the rule the footer already enforced).
 */
export function backChipIndex(
  chips: readonly BondChip[],
  current: number,
  /**
   * A chip that would bounce the same way and is skipped too. The page passes
   * the Indication chip when its decision is NOT GRANTED: that chip renders
   * nothing and auto-advances to the terms (BRK-S133), so landing on it would
   * put the broker straight back where they pressed Back (BRK-S197 fix).
   */
  passes: (chip: BondChip) => boolean = () => false,
): number {
  let target = current - 1;
  while (target > 1) {
    const chip = chips[target];
    if (!chip || (chip.kind !== "preapproval_checks" && !passes(chip))) break;
    target -= 1;
  }
  return Math.max(target, 1);
}

/**
 * What the Checks bar shows, derived from the run FOLD and nothing else.
 *
 * DELIBERATELY BLIND TO THE ROWS. A `data_pull` step renders one row per
 * orchestration, with a verdict on each, because a broker filling in a bond
 * request needs to know which bureau is slow and which referred them. A program
 * preapproval is a different thing: which bureaus Signal consults, and what
 * each said, is Signal's underwriting business. So the run's `checks` are on the
 * wire, are not read here, and cannot be — this takes the fold.
 *
 * NO OUTCOME WORDS EITHER. `outcome` decides whether the broker gets an
 * indication or a not-granted notice, which happens on the NEXT chip and in
 * Signal's own words. A settled bar reads the same for `pass` and for `fail`
 * (asserted, not merely intended).
 *
 * The percentages are honest about their own precision: this is a
 * determinate-LOOKING bar over a fold with four members, not a measurement.
 * They only have to rise.
 */
export interface PreapprovalProgress {
  /** 1–100. Never 0: the run has been requested, so something IS happening. */
  percent: number;
  label: string;
  settled: boolean;
}

export function preapprovalProgress(
  run: BondStepRun | null,
): PreapprovalProgress {
  if (run === null) {
    return {
      percent: 8,
      label: "Starting your review…",
      settled: false,
    };
  }
  switch (run.status) {
    case "queued":
      return {
        percent: 25,
        label: "Your review is queued — this usually takes under a minute.",
        settled: false,
      };
    case "pending":
      return {
        percent: 50,
        label: "Reviewing your business…",
        settled: false,
      };
    case "running":
      return {
        percent: 70,
        label: "Reviewing your business…",
        settled: false,
      };
    case "settled":
      return {
        percent: 100,
        label: "Review complete — preparing your indication.",
        settled: true,
      };
  }
}
